import type { ClinicalRecordPrintPatient } from './clinical-record-print';
import {
  DOC_BASE_STYLES,
  DOC_FOOTER_STYLES,
  buildDocFooterHtml,
  buildDocHeaderHtml,
  dash,
  esc,
  openPrintWindow,
  resolveLogoDataUri,
  row,
  type Translate,
} from './print-shared';

export interface ImagingOrderStudy {
  study_kind: string;
  body_region?: string | null;
  findings?: string | null;
  interpretation?: string | null;
  performed_at?: string | null;
}

export interface ImagingOrderPrintInput {
  patient: ClinicalRecordPrintPatient;
  chief_complaint?: string | null;
  primary_diagnosis?: string | null;
  studies: ImagingOrderStudy[];
}

function buildStudyHtml(s: ImagingOrderStudy, index: number, t: Translate): string {
  const kind_label = esc(t(`clinical.study_kind_${s.study_kind}`));
  const performed = s.performed_at ? new Date(s.performed_at).toLocaleDateString() : null;
  const result_lines = [
    s.findings ? `<p class="row"><span class="lbl">Hallazgos:</span>${esc(s.findings)}</p>` : '',
    s.interpretation ? `<p class="row"><span class="lbl">Interpretación:</span>${esc(s.interpretation)}</p>` : '',
  ]
    .filter(Boolean)
    .join('');
  return `
    <li class="study-item">
      <p class="study-headline"><span class="study-n">${index + 1}.-</span> <strong>${kind_label}</strong>${
        s.body_region ? ` — Región: ${esc(s.body_region)}` : ''
      }</p>
      ${performed ? `<p class="study-meta">Realizado: ${esc(performed)}</p>` : ''}
      ${result_lines}
    </li>`;
}

function buildStudiesHtml(studies: ImagingOrderStudy[], t: Translate): string {
  if (studies.length === 0) return `<p class="empty">Sin estudios de imagen registrados.</p>`;
  return `<ol class="study-list">${studies.map((s, i) => buildStudyHtml(s, i, t)).join('')}</ol>`;
}

function buildMotivoHtml(chief_complaint: string | null | undefined, primary_diagnosis: string | null | undefined): string {
  if (!chief_complaint && !primary_diagnosis) return `<p class="empty">Sin motivo registrado.</p>`;
  return [
    chief_complaint ? row('Motivo de consulta', chief_complaint) : '',
    primary_diagnosis ? row('Diagnóstico presuntivo', primary_diagnosis) : '',
  ]
    .filter(Boolean)
    .join('');
}

function buildDocumentHtml(input: ImagingOrderPrintInput, t: Translate, logo_data_uri: string | null): string {
  const { patient } = input;
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Orden de imagenología — ${esc(patient.patient_name)}</title>
<style>
  ${DOC_BASE_STYLES}
  ${DOC_FOOTER_STYLES}
  .study-list { list-style: none; padding: 0; margin: 0; }
  .study-item { margin-bottom: 12px; }
  .study-headline { font-size: 13px; margin: 0 0 2px; }
  .study-n { color: #666; }
  .study-meta { font-size: 11px; color: #555; margin: 0 0 4px; }
</style>
</head>
<body>
  <div class="page">
    <div class="sheet">
      ${buildDocHeaderHtml({
        logo_data_uri,
        doc_title: 'Orden de Ultrasonido y Rx',
        corner_label: 'No. Expediente',
        corner_value: patient.pet_record_code,
      })}

      <div class="tutor-row">
        <span><span class="lbl">Tutor/Propietario:</span>${dash(patient.owner_name)}</span>
        <span><span class="lbl">Fecha:</span>${esc(patient.visit_date)}</span>
      </div>

      <p class="section-title">Reseña</p>
      <div class="resena-grid">
        ${row('Paciente', patient.patient_name)}
        ${row('Sexo', patient.sex_label)}
        ${row('Raza', patient.breed)}
        ${row('Especie', patient.species)}
        ${row('Edad', patient.age_label)}
        ${row('Peso', patient.weight_label)}
        ${row('Teléfono', patient.owner_phone)}
        ${row('Expediente', patient.pet_record_code)}
      </div>

      <div class="box">
        <p class="box-title">Motivo de la solicitud</p>
        ${buildMotivoHtml(input.chief_complaint, input.primary_diagnosis)}
      </div>

      <div class="box tall">
        <p class="box-title">Estudios de Imagen Solicitados</p>
        ${buildStudiesHtml(input.studies, t)}
      </div>

      <div class="signature-block">
        <p class="row">Médico solicitante: ${esc(patient.vet_name)}</p>
        <p class="row">Céd. Prof.: ${dash(patient.vet_license)}</p>
      </div>

      ${buildDocFooterHtml()}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Opens a print-ready "Orden de Ultrasonido y Rx" document — one imaging study order per
 * visit, reusing the same header/footer chrome as the prescription and clinical-record PDFs.
 * Returns false if the popup was blocked so the caller can toast an explanation.
 */
export async function printImagingOrder(input: ImagingOrderPrintInput, t: Translate): Promise<boolean> {
  const logo_data_uri = await resolveLogoDataUri();
  const html = buildDocumentHtml(input, t, logo_data_uri);
  return openPrintWindow(html);
}
